import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { A } from '@osmosis/tailwind-ui/components/Text';
import React from 'react';

import Image from 'next/image';
import type { ImageItem as ImageItemType } from '../../../types/composer';
import Title from '../block-ui/Title';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import RichText from '../block-ui/RichText';
import { getTailwindBorderColorForTheme } from '../../../lib/composer/schemas/theme';

type ImageItemProps = {
  image: ImageItemType;
  theme: string;
  topHeader?: boolean;
  outline?: boolean;
  justify?: 'left' | 'center' | 'right';
};

/**
 * Renders an image block with optional heading, subheading, and rich text.
 *
 * - If `image.url` is provided, the image is wrapped in a link.
 * - Displays heading, subheading, and rich text if provided in the `image` prop.
 * - Supports a special "tannerScriptHeading" with custom font styling.
 * @param {Object} props - The component props.
 * @param {ImageItemType} props.image - The image data and related content.
 * @param {string} props.theme - The theme to apply for styling.
 * @param {boolean} [props.topHeader] - Whether to render the heading as a top-level header.
 * @param {boolean} [props.outline=false] - Whether to apply an outline style to the image.
 * @returns {JSX.Element} The rendered image block component.
 */
export const ImageItem = ({
  image,
  theme,
  topHeader,
  outline = false,
  justify = 'center',
}: ImageItemProps) => {
  const borderColor = getTailwindBorderColorForTheme(theme);
  return (
    <Flex
      className={`max-w-[300px] flex-col items-${justify} gap-2${
        outline ? ` border-2 ${borderColor} rounded-2xl` : ''
      } p-4`}
    >
      {image.image &&
        (image.url ? (
          <A href={image.url} className="p-4">
            <Image
              src={image.image}
              alt={image.altText ?? ''}
              width={150}
              height={150}
            />
          </A>
        ) : (
          <Flex className="p-4">
            <Image
              src={image.image}
              alt={image.altText ?? ''}
              width={150}
              height={150}
            />
          </Flex>
        ))}
      {image.heading && (
        <Heading
          text={image.heading}
          topHeader={topHeader}
          theme={theme}
          textProps={`text-base text-${justify}`}
        />
      )}
      {!image.tannerScriptHeading && image.subheading && (
        <Subheading
          text={image.subheading}
          textProps={`text-base font-semibold `}
          topHeader={topHeader}
          alignment={justify}
        />
      )}
      {image.text && (
        <RichText html={image.text} theme={theme} alignment={justify} />
      )}
      {image.tannerScriptHeading && image.subheading && (
        <Flex className={`mt-2 h-[300px] flex-col justify-center`}>
          <Title
            text={image.tannerScriptHeading}
            textProps={{
              fontFamily: 'Tannerscript, sans-serif',
              fontSize: '4rem',
              color: 'primary',
            }}
          />

          <Subheading text={image.subheading} topHeader={topHeader} />
        </Flex>
      )}
    </Flex>
  );
};

export default ImageItem;
